import { useEffect, useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../api";
import { useAuth } from "../auth";
import QuickPaymentModal from "../components/QuickPaymentModal";
import AlertsPanel from "../components/AlertsPanel";
import CompanyScopeBar from "../components/CompanyScopeBar";

export default function Payments({ setActive, company, setCompany }) {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [f, setF] = useState({ q: "", direction: "", status: "" });
  const [quick, setQuick] = useState(null);

  const cq = company ? `?company=${encodeURIComponent(company)}` : "";
  const load = () => {
    api.get(`/payments${cq}`).then((r) => setItems(r.data)).catch(() => toast.error("Ödemeler yüklenemedi"));
    api.get(`/payments/alerts${cq}`).then((r) => setAlerts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [company]);

  const filtered = useMemo(() => items
    .filter((p) => (!f.direction || p.direction === f.direction) && (!f.status || p.status === f.status))
    .filter((p) => !f.q || [p.project_name, p.subcontractor_short, p.stage, p.description].join(" ").toLowerCase().includes(f.q.toLowerCase()))
    .sort((a, b) => (b.date || b.due_date || "").localeCompare(a.date || a.due_date || "")), [items, f]);

  const sum = (dir, st) => filtered.filter((p) => p.direction === dir && (!st || p.status === st) && p.currency === "TRY").reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <main className="content" data-testid="page-payments">
      <div className="page-head">
        <div>
          <span className="section-kicker">NAKİT HAREKETLERİ</span>
          <h1>Ödemeler ({items.length})</h1>
          <p>İşverenden alınan tahsilatlar ve alt yüklenicilere yapılan ödemeler tek panelde.</p>
        </div>
        {can("admin", "project_manager", "finance") && (
          <div className="head-actions">
            <button className="new-btn btn-green" onClick={() => setQuick({ direction: "incoming" })} data-testid="quick-incoming-button"><Plus size={15} /> Tahsilat Kaydet</button>
            <button className="new-btn" onClick={() => setQuick({ direction: "outgoing" })} data-testid="quick-outgoing-button"><Plus size={15} /> Alt Yük. Ödemesi</button>
          </div>
        )}
      </div>

      <CompanyScopeBar company={company} setCompany={setCompany} />

      <div className="stats-grid">
        <div className="stat"><div><div className="eyebrow">Tahsil Edilen (TL)</div><strong className="margin big">{money(sum("incoming", "Ödendi"))}</strong><span className="stat-detail">Gelen · Ödendi</span></div></div>
        <div className="stat"><div><div className="eyebrow">Alt Yük. Ödenen (TL)</div><strong>{money(sum("outgoing", "Ödendi"))}</strong><span className="stat-detail">Giden · Ödendi</span></div></div>
        <div className="stat"><div><div className="eyebrow">Bekleyen Giden (TL)</div><strong>{money(sum("outgoing", "Planlanan") + sum("outgoing", "Bekliyor"))}</strong><span className="stat-detail">Planlanan + Bekliyor</span></div></div>
        <div className="stat"><div><div className="eyebrow">Bekleyen Tahsilat (TL)</div><strong>{money(sum("incoming", "Planlanan") + sum("incoming", "Bekliyor"))}</strong><span className="stat-detail">Planlanan + Bekliyor</span></div></div>
      </div>

      {alerts && <AlertsPanel alerts={alerts} compact />}

      <div className="filter-bar">
        <div className="search-wrap"><Search size={15} /><input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Proje, alt yüklenici, açıklama ara..." data-testid="payments-search" /></div>
        <select className="filter-select" value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })} data-testid="payments-direction-filter">
          <option value="">Tüm yönler</option><option value="incoming">Gelen (tahsilat)</option><option value="outgoing">Giden (alt yük.)</option>
        </select>
        <select className="filter-select" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="payments-status-filter">
          <option value="">Tüm durumlar</option><option>Ödendi</option><option>Planlanan</option><option>Bekliyor</option>
        </select>
      </div>

      <section className="panel">
        <div className="project-table" data-testid="payments-table">
          <div className="table-row table-head pay-row"><span>YÖN</span><span>AŞAMA</span><span>PROJE / TARAF</span><span>TUTAR</span><span>TARİH / VADE</span><span>DURUM</span><span></span></div>
          {filtered.map((p, i) => (
            <div className="table-row pay-row" key={p.id} data-testid={`payments-row-${i}`}>
              <em className={`status ${p.direction === "incoming" ? "status-green" : "status-amber"}`}>{p.direction === "incoming" ? "Gelen" : "Giden"}</em>
              <span>{p.stage}</span>
              <div><b className="text-line">{p.project_name || "—"}</b><small className="text-line muted">{p.subcontractor_short || (p.direction === "incoming" ? "İşveren" : "")}{p.work_item_category ? ` · ${p.work_item_category}` : ""}</small></div>
              <b className="mono-small">{money(p.amount, p.currency)}</b>
              <div><b className="text-line mono-small">{p.date ? p.date.slice(0, 10) : "—"}</b>{p.due_date && <small className="text-line muted">vade {p.due_date.slice(0, 10)}</small>}</div>
              <em className={`status ${p.status === "Bekliyor" ? "status-amber" : p.status === "Planlanan" ? "status-blue" : "status-green"}`}>{p.status}</em>
              <button className="row-action" onClick={() => setActive("projects", p.project_id)} data-testid={`payments-open-${i}`}>›</button>
            </div>
          ))}
          {!filtered.length && <div className="empty-hint">Kayıt bulunamadı.</div>}
        </div>
      </section>
      {quick && <QuickPaymentModal preset={quick} close={() => setQuick(null)} onSaved={load} />}
    </main>
  );
}
